import React, { useState } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../stores/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import PageHelp from '../../components/PageHelp'

const DataImport: React.FC = () => {
  const { user } = useAuthStore()
  const { hasPermission } = usePermissions(user)
  const [importType, setImportType] = useState<'accounts' | 'journal-entries' | 'gl-entries'>('accounts')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isImporting, setIsImporting] = useState(false)
  const [importResult, setImportResult] = useState<any>(null)
  const [skipHeader, setSkipHeader] = useState(true)

  if (!hasPermission('erp_account', 'write')) {
    return (
      <div className="p-6">
        <p className="text-red-800">You don't have permission to import data.</p>
      </div>
    )
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const ext = file.name.split('.').pop()?.toLowerCase()
      if (!['csv', 'xlsx', 'xls'].includes(ext || '')) {
        toast.error('Please select a CSV or Excel file')
        return
      }
      setSelectedFile(file)
      setImportResult(null)
    }
  }

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`/api/erp/import/templates/${importType}`, {
        responseType: 'blob',
      })

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `${importType}-template.csv`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)

      toast.success('Template downloaded')
    } catch (error: any) {
      console.error('Error downloading template:', error)
      toast.error('Failed to download template')
    }
  }

  const handleImport = async () => {
    if (!selectedFile) {
      toast.error('Please select a file to import')
      return
    }

    setIsImporting(true)
    setImportResult(null)

    try {
      const formData = new FormData()
      formData.append('file', selectedFile)
      formData.append('skipHeader', skipHeader.toString())

      const endpoint = `/api/erp/import/${importType === 'journal-entries' ? 'journal-entries' : importType === 'gl-entries' ? 'gl-entries' : 'accounts'}`
      
      const response = await axios.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      setImportResult(response.data.data)
      
      if (response.data.success) {
        toast.success(`Import completed: ${response.data.data.imported} imported, ${response.data.data.failed} failed`)
      } else {
        toast.error(`Import completed with errors: ${response.data.data.imported} imported, ${response.data.data.failed} failed`)
      }
    } catch (error: any) {
      console.error('Error importing data:', error)
      toast.error(error.response?.data?.error || 'Failed to import data')
      if (error.response?.data?.data) {
        setImportResult(error.response.data.data)
      }
    } finally {
      setIsImporting(false)
    }
  }

  const getImportTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      accounts: 'Chart of Accounts',
      'journal-entries': 'Journal Entries',
      'gl-entries': 'GL Entries (Migration)',
    }
    return labels[type] || type
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">Data Import</h1>
          <PageHelp pageIdentifier="data-import" title="Data Import" />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6 max-w-4xl">
        <div className="space-y-6">
          {/* Import Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Import Type</label>
            <select
              value={importType}
              onChange={(e) => {
                setImportType(e.target.value as any)
                setSelectedFile(null)
                setImportResult(null)
              }}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="accounts">Chart of Accounts</option>
              <option value="journal-entries">Journal Entries</option>
              <option value="gl-entries">GL Entries (Migration)</option>
            </select>
            <p className="mt-1 text-sm text-gray-500">
              {importType === 'accounts' && 'Import your chart of accounts from CSV/Excel'}
              {importType === 'journal-entries' && 'Import journal entries from CSV/Excel'}
              {importType === 'gl-entries' && 'Import GL entries for data migration (use with caution)'}
            </p>
          </div>

          {/* Download Template */}
          <div>
            <button
              onClick={handleDownloadTemplate}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-sm"
            >
              Download Template CSV
            </button>
            <p className="mt-1 text-sm text-gray-500">
              Download a template file to see the required format
            </p>
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select File</label>
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileSelect}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
            {selectedFile && (
              <p className="mt-1 text-sm text-gray-600">
                Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
              </p>
            )}
          </div>

          {/* Options */}
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={skipHeader}
                onChange={(e) => setSkipHeader(e.target.checked)}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Skip header row (first row contains column names)</span>
            </label>
          </div>

          {/* Import Button */}
          <div>
            <button
              onClick={handleImport}
              disabled={!selectedFile || isImporting}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isImporting ? 'Importing...' : `Import ${getImportTypeLabel(importType)}`}
            </button>
          </div>

          {/* Import Results */}
          {importResult && (
            <div className="mt-6 border-t pt-6">
              <h3 className="text-lg font-semibold mb-4">Import Results</h3>
              
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="bg-green-50 p-4 rounded">
                  <div className="text-2xl font-bold text-green-600">{importResult.imported}</div>
                  <div className="text-sm text-gray-600">Imported</div>
                </div>
                <div className="bg-red-50 p-4 rounded">
                  <div className="text-2xl font-bold text-red-600">{importResult.failed}</div>
                  <div className="text-sm text-gray-600">Failed</div>
                </div>
                <div className="bg-yellow-50 p-4 rounded">
                  <div className="text-2xl font-bold text-yellow-600">{importResult.warnings?.length || 0}</div>
                  <div className="text-sm text-gray-600">Warnings</div>
                </div>
              </div>

              {importResult.errors && importResult.errors.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-semibold text-red-600 mb-2">Errors</h4>
                  <div className="bg-red-50 border border-red-200 rounded p-4 max-h-60 overflow-y-auto">
                    <ul className="list-disc list-inside space-y-1">
                      {importResult.errors.map((error: any, idx: number) => (
                        <li key={idx} className="text-sm text-red-800">
                          Row {error.row}: {error.error}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {importResult.warnings && importResult.warnings.length > 0 && (
                <div>
                  <h4 className="font-semibold text-yellow-600 mb-2">Warnings</h4>
                  <div className="bg-yellow-50 border border-yellow-200 rounded p-4 max-h-60 overflow-y-auto">
                    <ul className="list-disc list-inside space-y-1">
                      {importResult.warnings.map((warning: any, idx: number) => (
                        <li key={idx} className="text-sm text-yellow-800">
                          {warning.row > 0 ? `Row ${warning.row}: ` : ''}{warning.warning}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {importResult.success && importResult.imported > 0 && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded">
                  <p className="text-green-800 font-semibold">
                    ✅ Import completed successfully! {importResult.imported} {importResult.imported === 1 ? 'item' : 'items'} imported.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default DataImport
